import { useEffect, useRef, useState } from 'react'
import { graphql } from 'relay-runtime'
import { useLazyLoadQuery, useMutation } from 'react-relay'
import { useUserStore } from '@/defines/store/userStore'
import type { aiConsultDetailQuery as AiConsultQueryType } from './__generated__/aiConsultDetailQuery.graphql'
import type { aiConsultDetailUpdateChatMutation as AiConsultUpdateType } from './__generated__/aiConsultDetailUpdateChatMutation.graphql'
import userImage from 'assets/user.png'
import { AiConsultStatus, AiConsultStatusName, ChatViewModel } from '@/defines/requestDefines'
import { ChatBot } from '@/components/chatBot'
import { ChatMessage } from 'ai/react'
import CommonService from '@/defines/service/commonService'
import { CustomMarkdown } from '@/components/common/CustomMarkdown'
import { MarkdownToPdfService } from '@/defines/service/markdownToPdfService'
import { PDFViewer } from '@react-pdf/renderer';
import { Buffer } from 'buffer';

window.Buffer = Buffer;

const AiConsultDetailUpdateChatMutation = graphql`
mutation aiConsultDetailUpdateChatMutation($linkparameter: String!, $chatHistory: String!){
    update_request_ai_consult_detail(where: {linkparameter: {_eq: $linkparameter}}, _set: {chatHistory: $chatHistory}) {
        affected_rows
    }
}
`

const AiConsultDetailUpdateStatusMutation = graphql`
mutation aiConsultDetailUpdateStatusMutation($linkparameter: String!, $status: Int!){
    update_request_ai_consult_detail(where: {linkparameter: {_eq: $linkparameter}}, _set: {status: $status}) {
        affected_rows
    }
}
`

const AiConsultDetailQuery = graphql`
query aiConsultDetailQuery($linkparameter: String!, $recipientid: String!){
  insurance_request_log_connection(where: {request_ai_consult_details: {linkparameter: {_eq: $linkparameter}}, recipientid: {_eq: $recipientid}}) {
    edges {
      node {
        request_ai_consult_details {
            id
            linkparameter
            startchatdate
            created_at
            chatHistory
            askingotherthingscounter
            customerfeedback
            customerrating
            questionsummary
            reportmarkdown
            reportpublishdate
            status
            messages
        }
        recipient {
          id
          email
          imageurl
          last_seen
          name
        }
        requeststatus
        requesttype
        recipientid
        insuranceid
        id
        created_at
        caredetailid
        aiconsultdetailid

        insurance {
          id
          name
          user {
            name
            email
            imageurl
          }
        }
      }
    }
  }
}
`

interface RequestChatViewModel {
    aiconsultdetailid: number | null | undefined;
    caredetailid: number | null | undefined;
    created_at: Date | null | undefined;
    id: string;
    insuranceid: string;
    recipientid: string | null | undefined;
    request_ai_consult_details: {
        created_at: any;
        id: string;
        linkparameter: string;
        startchatdate: any;
        chatHistory: string;
        askingotherthingscounter: number;
        customerfeedback: string | null | undefined;
        customerrating: number;
        questionsummary: string | null | undefined;
        reportmarkdown: string | null | undefined;
        reportpublishdate: any;
        status: number | null | undefined;
        messages: ChatMessage[] | null | undefined;
    } | null | undefined;
    recipient: {
        email: string | null | undefined;
        id: string;
        imageurl: string | null | undefined;
        last_seen: any;
        name: string;
    } | null | undefined;
    requeststatus: number;
    requesttype: number;
    insurance: {
        id: string;
        name: string;
        user: {
            imageurl: string | null | undefined;
            name: string | null | undefined;
            email: string | null | undefined;
        } | null | undefined;
    } | null | undefined;
}

export default function AiConsult() {
    const [data, setData] = useState({} as RequestChatViewModel);
    const [messages, setMessages] = useState([] as ChatViewModel[]);
    const userId = useUserStore.getState().userData.id;
    const [commitUpdate] = useMutation<AiConsultUpdateType>(AiConsultDetailUpdateChatMutation);
    const [commitStatusUpdate] = useMutation(AiConsultDetailUpdateStatusMutation);

    const search = window.location.search
    const params = new URLSearchParams(search)
    const linkparameter = params.get('linkparameter')

    if (!linkparameter) return;

    const aiConsultData = useLazyLoadQuery<AiConsultQueryType>(
        AiConsultDetailQuery,
        {
            recipientid: userId,
            linkparameter: linkparameter ?? ''
        },
    );


    useEffect(() => {
        console.log(aiConsultData);
        if (aiConsultData?.insurance_request_log_connection?.edges.length == 0) {
            return;
        }
        aiConsultData?.insurance_request_log_connection?.edges.forEach(item => {
            let forAdd = {} as RequestChatViewModel;
            if (item.node.request_ai_consult_details) {
                forAdd.request_ai_consult_details = {
                    created_at: item.node.request_ai_consult_details.created_at,
                    id: item.node.request_ai_consult_details.id,
                    linkparameter: item.node.request_ai_consult_details.linkparameter,
                    startchatdate: item.node.request_ai_consult_details.startchatdate,
                    chatHistory: item.node.request_ai_consult_details.chatHistory,
                    askingotherthingscounter: item.node.request_ai_consult_details.askingotherthingscounter,
                    customerfeedback: item.node.request_ai_consult_details.customerfeedback,
                    customerrating: item.node.request_ai_consult_details.customerrating,
                    questionsummary: item.node.request_ai_consult_details.questionsummary,
                    reportmarkdown: item.node.request_ai_consult_details.reportmarkdown,
                    reportpublishdate: item.node.request_ai_consult_details.reportpublishdate,
                    status: item.node.request_ai_consult_details.status,
                    messages: item.node.request_ai_consult_details.messages,
                };
                
                if (forAdd.request_ai_consult_details.reportpublishdate && 
                    new Date(forAdd.request_ai_consult_details.reportpublishdate) <= new Date() &&
                    forAdd.request_ai_consult_details.status === AiConsultStatus.questionSummarized) {
                    forAdd.request_ai_consult_details.status = AiConsultStatus.reportGenerated;
                    
                    commitStatusUpdate({
                        variables: {
                            linkparameter: linkparameter,
                            status: AiConsultStatus.reportGenerated,
                        },
                        onCompleted: () => {
                            console.log('Status updated successfully');
                        },
                        onError: (error) => {
                            console.error('Error updating status:', error);
                        }
                    });
                }
                
                console.log('forAdd.request_ai_consult_details.id', CommonService().DecodeNodeId(forAdd.request_ai_consult_details.id));
            }
            if (item.node.recipient) {
                forAdd.recipient = {
                    email: item.node.recipient.email,
                    id: item.node.recipient.id,
                    imageurl: item.node.recipient.imageurl,
                    last_seen: item.node.recipient.last_seen,
                    name: item.node.recipient.name,
                };
            }
            if (item.node.insurance) {
                forAdd.insurance = {
                    id: item.node.insurance.id,
                    user: {
                        imageurl: item.node.insurance.user?.imageurl,
                        email: item.node.insurance.user?.email,
                        name: item.node.insurance.user?.name,
                    },
                    name: item.node.insurance.name,
                };
            }
            forAdd.id = item.node.id;
            forAdd.aiconsultdetailid = item.node.aiconsultdetailid;
            forAdd.caredetailid = item.node.caredetailid;
            forAdd.created_at = item.node.created_at;
            forAdd.insuranceid = item.node.insuranceid;
            forAdd.recipientid = item.node.recipientid;
            forAdd.requeststatus = item.node.requeststatus;
            forAdd.requesttype = item.node.requesttype;
            setData(forAdd);
        });

    }, [aiConsultData])

    useEffect(() => {
        if (data.request_ai_consult_details?.chatHistory) {
            let chats: ChatViewModel[] = JSON.parse(data.request_ai_consult_details?.chatHistory);
            if (chats && chats.length > 0) {
                setMessages(chats);
            }
        }
    }, [data])


    const messagesEndRef = useRef<HTMLDivElement>(null);
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        scrollToBottom();

        if (messages.length > 0) {
            commitUpdate({
                variables: {
                    linkparameter: linkparameter,
                    chatHistory: JSON.stringify(messages),
                },
                onCompleted: () => {
                    console.log('onCompleted');
                },
                onError: (error) => {
                    console.error('Error updating user', error);
                }
            });
        }

    }, [messages]);

    return (
        <div className="bg-white">
            <div className="mx-auto" style={{ "maxWidth": "1400px" }}>
                <div className="md:grid md:grid-cols-12 max-sm:flex max-sm:flex-col mt-12">
                    <div className="md:col-span-3 p-2">
                        {/* <img
                            src={aiConsultImage}
                            style={{ maxWidth: '100%', maxHeight: '60vh' }}
                            className="object-cover object-center rounded-xl"
                        /> */}

                        <h3 className="mt-3 text-xl font-bold tracking-tight text-gray-900">基本資訊</h3>
                        <div className="mt-3 sm:col-span-3 grid grid-cols-1 gap-x-6 gap-y-3">
                            <div>
                                <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">
                                    狀態
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="city"
                                        name="city"
                                        type="text"
                                        value={AiConsultStatusName[data.request_ai_consult_details?.status ?? 0].Name}
                                        disabled={true}
                                        // onChange={(e) => setMedicalCenter({ ...medicalCenter, city: e.target.value })}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm 
                                            ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset 
                                            focus:ring-red-400 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">
                                    開始時間
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="city"
                                        name="city"
                                        type="text"
                                        value={new Date(data.request_ai_consult_details?.startchatdate).toLocaleString()}
                                        disabled={true}
                                        // onChange={(e) => setMedicalCenter({ ...medicalCenter, city: e.target.value })}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm 
                                            ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset 
                                            focus:ring-red-400 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">
                                    協助之業務員
                                </label>
                                <div className="flex justify-between mt-2">
                                    <img className='mx-4 size-12 flex-none rounded-full bg-gray-50' src={data.insurance?.user?.imageurl ?? userImage} />
                                    <input
                                        id="city"
                                        name="city"
                                        type="text"
                                        value={data.insurance?.user?.name ?? data.insurance?.user?.email ?? ""}
                                        disabled={true}
                                        // onChange={(e) => setMedicalCenter({ ...medicalCenter, city: e.target.value })}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm 
                                            ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset 
                                            focus:ring-red-400 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                        </div>

                    </div>
                    <div className="md:col-span-9 max-sm:order-last px-4 mb-6">
                        {
                            data.request_ai_consult_details?.status == AiConsultStatus.chating ?
                                <ChatBot
                                    endpoint="asking-question"
                                    aiConsultDetailId={CommonService().DecodeNodeId(data.request_ai_consult_details.id)}
                                    initialMessages={data.request_ai_consult_details.messages ?? [] as ChatMessage[]} /> : <></>
                        }
                        {
                            data.request_ai_consult_details?.status == AiConsultStatus.questionSummarized ?
                                <>
                                    <div className="mt-3 mb-12">
                                        <h3 className="text-xl font-bold tracking-tight text-gray-900">問題摘要</h3>
                                        <div className="mt-2">
                                            <CustomMarkdown content={data.request_ai_consult_details?.questionsummary ?? ""} />
                                        </div>
                                        <div className="mt-2">
                                            <p className="text-sm/6 text-gray-900">我們將盡快審閱您的問題並詢問AI目前情況。</p>
                                        </div>
                                    </div>
                                </> : <></>
                        }
                        {
                            data.request_ai_consult_details?.status == AiConsultStatus.reportGenerated ?
                                <>
                                    <div className='mt-3 mb-12'>
                                        <PDFViewer style={{ width: '100%', height: '600px' }}>
                                            {
                                                MarkdownToPdfService.convertToMedicalReport(data.request_ai_consult_details?.reportmarkdown ?? "")
                                            }
                                        </PDFViewer>
                                    </div>
                                </> : <></>
                        }
                    </div>
                </div>
            </div >

        </div >

    )
}
